import { HISTORY_ACTION_MODE, ROOT_NODE } from '@candulabs/craft';
import { css, styled } from 'canoe';
import React, { KeyboardEventHandler, useState } from 'react';

import { Flags, useFlags } from 'commons/hooks';
import { CssSelectorId } from 'craft/types';

import { EditSpacingPopup } from './EditSpacingPopup';
import { SpacingAirHandler } from './SpacingAir';
import SpacingBox from './SpacingBox';
import ValueLink from './ValueLink';

const Container = styled.div`
  position: relative;
  width: fit-content;
`;

const ValueLabel = styled.div<{ isActive?: boolean; disabled: boolean }>`
  ${({ theme, isActive, disabled }) => css`
    position: absolute;
    min-width: 12px;
    height: 19px;
    font-size: 11px;
    line-height: 17px;
    text-align: center;
    color: ${theme.colors.OnyxGray};
    background-color: ${theme.colors.GlitterGrayL20};
    border: 1px solid ${theme.colors.Transparent};
    border-radius: ${theme.borderRadius.small};
    padding: 0 2px;
    overflow: hidden;
    text-transform: capitalize;
    cursor: pointer;

    ${isActive &&
    css`
      border-color: ${theme.colors.Success};
    `}

    ${disabled &&
    css`
      color: ${theme.colors.GlitterGray};
      border-color: ${theme.colors.Transparent};
      cursor: not-allowed;
    `}
  `}
`;

export type CSSValue = string | null | number;

export interface SpacingCSS {
  marginLeft: CSSValue;
  paddingLeft: CSSValue;
  marginBottom: CSSValue;
  paddingBottom: CSSValue;
  marginTop: CSSValue;
  paddingTop: CSSValue;
  marginRight: CSSValue;
  paddingRight: CSSValue;
}

interface SpacingProps {
  cssProps: Partial<SpacingCSS>;
  setCss: (
    css: Partial<SpacingCSS>,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => void;
  isMarginEditable: boolean;
  isPaddingEditable: boolean;
  disabled?: boolean;
  nodeIds?: string[];
  cssSelectorId?: CssSelectorId;
}

const Spacing = ({
  cssProps,
  setCss,
  disabled,
  nodeIds,
  isMarginEditable,
  isPaddingEditable,
  cssSelectorId,
}: SpacingProps) => {
  const { [Flags.SPACING_SELECT]: showSpacingOnSelect } = useFlags();
  const [linkState, setLinkState] = useState({ margin: false, padding: false });
  const [editedProperty, setEditedProperty] = useState<keyof SpacingCSS | null>(null);
  const {
    marginLeft,
    paddingLeft,
    marginBottom,
    paddingBottom,
    marginTop,
    paddingTop,
    marginRight,
    paddingRight,
  } = cssProps;

  const [deletedProperty, setDeletedProperty] = useState<keyof SpacingCSS | null>(null);

  const setValue = (value: string | undefined, propName: string) => {
    if (isMarginEditable && linkState.margin && propName.indexOf('margin') >= 0) {
      // set all margins to this value
      setCss(
        {
          marginTop: value,
          marginRight: value,
          marginBottom: value,
          marginLeft: value,
        },
        HISTORY_ACTION_MODE.THROTTLE,
        500,
      );

      return;
    }

    if (isPaddingEditable && linkState.padding && propName.indexOf('padding') >= 0) {
      // set all paddings to this value
      setCss(
        {
          paddingTop: value,
          paddingRight: value,
          paddingBottom: value,
          paddingLeft: value,
        },
        HISTORY_ACTION_MODE.THROTTLE,
        500,
      );
      return;
    }

    if (
      (isMarginEditable && propName.indexOf('margin') >= 0) ||
      (isPaddingEditable && propName.indexOf('padding') >= 0)
    ) {
      setCss({ [propName]: value }, HISTORY_ACTION_MODE.THROTTLE, 500);
    }
  };

  const onOutsideClick = () => {
    if (editedProperty !== null) {
      setEditedProperty(null);
      setDeletedProperty(null);
    }
  };

  const onKeyDown: KeyboardEventHandler = (e) => {
    if ((e.key === 'Enter' || e.key === 'Escape') && editedProperty !== null) {
      setEditedProperty(null);
      setDeletedProperty(null);
    }
  };

  const isMarginDisabled = disabled || !isMarginEditable;
  const isPaddingDisabled = disabled || !isPaddingEditable;

  return (
    <>
      {(showSpacingOnSelect || editedProperty) && nodeIds && (
        <SpacingAirHandler
          key="spacing"
          cssProperties={cssProps}
          nodeIds={nodeIds}
          cssSelectorId={cssSelectorId}
        />
      )}
      <Container>
        <SpacingBox />

        {editedProperty && nodeIds && (
          <EditSpacingPopup
            onClose={onOutsideClick}
            setValue={(value?: string) => setValue(value, editedProperty)}
            onKeyDown={onKeyDown}
            showAuto={
              !nodeIds?.includes(ROOT_NODE) &&
              (editedProperty.includes('marginLeft') || editedProperty.includes('marginRight'))
            }
            showPercentage={!(nodeIds?.includes(ROOT_NODE) && editedProperty.includes('margin'))}
            allowNegativeValues={editedProperty.includes('margin')}
            value={cssProps[editedProperty]}
            showAsEmpty={(empty: boolean) => {
              if (empty) {
                setDeletedProperty(editedProperty);
              } else {
                setDeletedProperty(null);
              }
            }}
          />
        )}

        <ValueLink
          key="margin"
          isLinked={linkState.margin}
          disabled={isMarginDisabled}
          onClick={() => setLinkState({ ...linkState, margin: !linkState.margin })}
          css={{ top: 2, left: 20 }}
        >
          Margin
        </ValueLink>
        <ValueLink
          key="padding"
          isLinked={linkState.padding}
          disabled={isPaddingDisabled}
          onClick={() => setLinkState({ ...linkState, padding: !linkState.padding })}
          css={{ top: 34, left: 65 }}
        >
          Padding
        </ValueLink>

        {/* MARGINS */}

        <ValueLabel
          key="margin-left"
          css={{ top: '50%', transform: 'translate(-50%, -50%)', left: '16px' }}
          onClick={() => !isMarginDisabled && setEditedProperty('marginLeft')}
          disabled={isMarginDisabled}
          isActive={editedProperty === 'marginLeft'}
        >
          {deletedProperty === 'marginLeft' ? '' : (marginLeft || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="margin-top"
          css={{ top: 7, left: '50%', transform: 'translate(-50%, 0)' }}
          onClick={() => !isMarginDisabled && setEditedProperty('marginTop')}
          disabled={isMarginDisabled}
          isActive={editedProperty === 'marginTop'}
        >
          {deletedProperty === 'marginTop' ? '' : (marginTop || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="margin-right"
          css={{ top: '50%', transform: 'translate(50%, -50%)', right: '16px' }}
          onClick={() => !isMarginDisabled && setEditedProperty('marginRight')}
          disabled={isMarginDisabled}
          isActive={editedProperty === 'marginRight'}
        >
          {deletedProperty === 'marginRight' ? '' : (marginRight || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="margin-bottom"
          css={{ bottom: 7, left: '50%', transform: 'translate(-50%, 0)' }}
          onClick={() => !isMarginDisabled && setEditedProperty('marginBottom')}
          disabled={isMarginDisabled}
          isActive={editedProperty === 'marginBottom'}
        >
          {deletedProperty === 'marginBottom'
            ? ''
            : (marginBottom || 0).toString().replace('px', '')}
        </ValueLabel>

        {/* PADDINGS */}

        <ValueLabel
          key="padding-left"
          css={{ top: '50%', transform: 'translate(-50%, -50%)', left: '52px' }}
          onClick={() => !isPaddingDisabled && setEditedProperty('paddingLeft')}
          disabled={isPaddingDisabled}
          isActive={editedProperty === 'paddingLeft'}
        >
          {deletedProperty === 'paddingLeft' ? '' : (paddingLeft || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="padding-top"
          css={{ top: 33, left: '50%', transform: 'translate(-50%, 0)' }}
          onClick={() => !isPaddingDisabled && setEditedProperty('paddingTop')}
          disabled={isPaddingDisabled}
          isActive={editedProperty === 'paddingTop'}
        >
          {deletedProperty === 'paddingTop' ? '' : (paddingTop || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="padding-right"
          css={{ top: '50%', transform: 'translate(50%, -50%)', right: '52px' }}
          onClick={() => !isPaddingDisabled && setEditedProperty('paddingRight')}
          disabled={isPaddingDisabled}
          isActive={editedProperty === 'paddingRight'}
        >
          {deletedProperty === 'paddingRight'
            ? ''
            : (paddingRight || 0).toString().replace('px', '')}
        </ValueLabel>
        <ValueLabel
          key="padding-bottom"
          css={{ bottom: 33, left: '50%', transform: 'translate(-50%, 0)' }}
          onClick={() => !isPaddingDisabled && setEditedProperty('paddingBottom')}
          disabled={isPaddingDisabled}
          isActive={editedProperty === 'paddingBottom'}
        >
          {deletedProperty === 'paddingBottom'
            ? ''
            : (paddingBottom || 0).toString().replace('px', '')}
        </ValueLabel>
      </Container>
    </>
  );
};

export default Spacing;
